<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  consumed: { type: Number, required: true },
  allowed: { type: Number, required: true },
  resourceKey: { type: String, required: true },
});

const { t } = useI18n();
const router = useRouter();
const { accountId } = useAccount();

const limitReached = computed(() => props.consumed >= props.allowed);
const nearLimit = computed(
  () => props.allowed > 0 && props.consumed / props.allowed >= 0.8
);
const shouldShow = computed(() => nearLimit.value || limitReached.value);

const message = computed(() => {
  if (limitReached.value) {
    return t('BILLING_SETTINGS.LIMIT_BANNER.REACHED', {
      resource: t(
        `BILLING_SETTINGS.LIMIT_BANNER.RESOURCES.${props.resourceKey}`
      ),
      consumed: props.consumed,
      allowed: props.allowed,
    });
  }
  return t('BILLING_SETTINGS.LIMIT_BANNER.NEAR', {
    resource: t(`BILLING_SETTINGS.LIMIT_BANNER.RESOURCES.${props.resourceKey}`),
    consumed: props.consumed,
    allowed: props.allowed,
  });
});

const goToBilling = () => {
  router.push({
    name: 'billing_settings_index',
    params: { accountId: accountId.value },
  });
};
</script>

<template>
  <div
    v-if="shouldShow"
    class="flex items-center justify-between gap-3 px-4 py-3 rounded-lg"
    :class="limitReached ? 'bg-n-ruby-3' : 'bg-n-amber-3'"
  >
    <p
      class="text-sm"
      :class="limitReached ? 'text-n-ruby-11' : 'text-n-amber-11'"
    >
      {{ message }}
    </p>
    <Button
      sm
      solid
      :blue="!limitReached"
      :ruby="limitReached"
      @click="goToBilling"
    >
      {{ $t('BILLING_SETTINGS.LIMIT_BANNER.MANAGE_BILLING') }}
    </Button>
  </div>
</template>
